import React, { useEffect, useState } from 'react';
import {
  Grid,
  Typography,
  Switch,
  Box,
  CircularProgress
} from '@mui/material';
import { useCardContext } from 'contexts/CardContext';
import { useParams } from 'react-router-dom';
import { updateLeadCaptureMode, getCardById } from 'apis/cardApi';
import NotificationSnackbar from 'components/common/notification/NotificationSnackbar';
import { useAuth } from 'contexts/AuthContext';

const LeadCaptureFormContainer = () => {
  const { cardState, setLeadCaptureModeOn } = useCardContext();
  const { cardId } = useParams();
  const { authState } = useAuth();
  const userId = authState.user?.userId;

  // EXPLICIT STATE DECLARATIONS
  const [isInitialized, setIsInitialized] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  // Always provide a boolean value to prevent controlled/uncontrolled warnings
  const leadCaptureModeOn = cardState[cardId]?.leadCaptureModeOn ?? false;

  // Fetch card data and initialize settings
  useEffect(() => {
    const fetchCardData = async () => {
      if (!cardId || isInitialized) return;

      try {
        setIsLoading(true);

        // ✅ FIX: Check if this is a new card creation (UUID)
        const isNewCardCreation = cardId.includes('-') && cardId.length >= 32 && cardId.length <= 36;

        if (isNewCardCreation) {
          // ✅ NEW CARD: Use default value (false) for lead capture mode
          console.log('🔍 LeadCapture: New card creation - using default value (false)');
          setLeadCaptureModeOn(cardId, false);
        } else {
          // ✅ EXISTING CARD: Get card-specific setting from server
          const response = await getCardById(cardId);

          if (response && response.card) {
            const cardSetting = response.card.leadCaptureModeOn;
            console.log('🔍 LeadCapture: Server value:', cardSetting);

            // Update CardContext with server value
            setLeadCaptureModeOn(cardId, Boolean(cardSetting));
          }
        }

        setIsInitialized(true);
      } catch (error) {
        console.error('Failed to load lead capture settings:', error);
        // ✅ FALLBACK: Use default value on error
        setLeadCaptureModeOn(cardId, false);
      } finally {
        setIsLoading(false);
      }
    };

    fetchCardData();
  }, [cardId, userId, isInitialized, setLeadCaptureModeOn]);

  // Handle toggle change
  const handleToggle = (event) => {
    if (isLoading) return;

    const isChecked = event.target.checked;
    console.log("Toggle switched:", isChecked);

    // Update local state first for immediate feedback
    setLeadCaptureModeOn(cardId, isChecked);

    // Update on the server using dedicated API
    updateLeadCaptureMode(cardId, isChecked)
      .then(() => {
        console.log("API call successful");

        setSnackbar({
          open: true,
          message: isChecked ? 'Lead capture mode enabled' : 'Lead capture mode disabled',
          severity: 'success'
        });
      })
      .catch(error => {
        console.error("API call failed:", error);
        // Revert on error
        setLeadCaptureModeOn(cardId, !isChecked);

        setSnackbar({
          open: true,
          message: 'Failed to update lead capture mode',
          severity: 'error'
        });
      });
  };

  // Log lead capture mode only after initialization
  useEffect(() => {
    if (isInitialized) {
      console.log("leadCaptureModeOn:", leadCaptureModeOn);
    }
  }, [leadCaptureModeOn, isInitialized]);

  // ✅ CHECK: Hide lead capture form during new card creation
  const isNewCardCreation = cardId && cardId.includes('-') && cardId.length >= 32 && cardId.length <= 36;

  // ✅ HIDE: Don't render anything during new card creation
  if (isNewCardCreation) {
    return null;
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        overflow: 'hidden',
        position: 'relative'
      }}
    >
      {/* Header */}
      <Box
        sx={{
          padding: 2,
          paddingBottom: 0,
          backgroundColor: 'white',
          borderBottom: '1px solid #e1e1e1',
          flexShrink: 0
        }}
      >
        <Typography
          variant="h6"
          component="h2"
          sx={{
            fontWeight: 'bold',
            marginBottom: 2
          }}
        >
          Lead Capture
        </Typography>
      </Box>

      {/* Content */}
      <Box
        sx={{
          flex: 1,
          overflowY: 'auto',
          overflowX: 'hidden',
          backgroundColor: 'white'
        }}
      >
        <Box sx={{ padding: 2 }}>
          <Grid container alignItems="center" spacing={2}>
            <Grid item xs={12} sm={8}>
              <Typography
                variant="body1"
                sx={{
                  fontSize: '0.8rem',
                  fontWeight: 600,
                  color: "#828282"
                }}
              >
                When lead capture mode is enabled, the lead form will pop up as soon as your profile is shared.
              </Typography>
            </Grid>

            <Grid
              item
              xs={12}
              sm={4}
              display="flex"
              justifyContent={{ xs: 'flex-start', sm: 'flex-end' }}
            >
              {isLoading ? (
                <CircularProgress size={24} />
              ) : (
                <Switch
                  checked={Boolean(leadCaptureModeOn)}
                  onChange={handleToggle}
                />
              )}
            </Grid>
          </Grid>
        </Box>
      </Box>

      {/* Snackbar */}
      <NotificationSnackbar
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        handleClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
      />
    </Box>
  );
};

export default LeadCaptureFormContainer;